import React, { useMemo } from 'react';
import { Box, Typography, Switch, TextField } from '@material-ui/core';
import { Field } from '@campfire/field';
import { Form, Formik } from 'formik';
import { TabletButton } from '@campfire/tablet-button';
import { Autocomplete } from '@material-ui/lab';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { ColourPickerSelect } from '../../../../../common/icons/ColourPickerSelect';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { AdminConsoleVolunteerTag } from './__generated__/AdminConsoleVolunteerTag';

const DEFAULT_TAG_COLOUR = '#968EFF';

interface Props {
  tag?: AdminConsoleVolunteerTag;
  existingGroups: string[];
  onClose: () => void;
  onSuccess: (message: string) => void;
}

interface VolunteerTagFormValues {
  name: string;
  description: string;
  colour: string;
  tagGroup: string;
  isArchived: boolean;
}

export function FormVolunteerTagDialog({ tag, existingGroups, onClose, onSuccess }: Props) {
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });
  const [error, setError] = React.useState<string | null>(null);

  const initialValues = useMemo(
    () => ({
      name: tag?.name ?? '',
      description: tag?.description ?? '',
      colour: tag?.colour ?? DEFAULT_TAG_COLOUR,
      tagGroup: tag?.tagGroup ?? '',
      isArchived: tag?.isArchived ?? false,
    }),
    [tag]
  );

  const handleSubmit = (vals: VolunteerTagFormValues) => {
    const isUpdate = Boolean(tag?.volunteerTagId);
    campfireMutation
      .run({
        url: `${apiUrl}/vm/volunteer-tags/save`,
        method: 'post',
        data: {
          ...(isUpdate ? { volunteerTagId: tag?.volunteerTagId } : {}),
          name: vals.name,
          description: vals.description || null,
          colour: vals.colour || null,
          tagGroup: vals.tagGroup ? vals.tagGroup.trim() : null,
          isArchived: vals.isArchived,
        },
      })
      .then((res: any) => {
        if (!res?.ok) {
          setError('Unable to save tag. Please try again.');
          return;
        }
        onSuccess(isUpdate ? 'Tag updated' : 'A new tag has been created!');
      })
      .catch(() => setError('Unable to save tag. Please try again.'));
  };

  return (
    <FullscreenDialog open close={onClose} title={tag ? 'Edit User Tag' : 'Add User Tag'} maxWidth='xs' fullWidth>
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => (
          <Form>
            <Box mt='20px'>
              <Box display='flex' style={{ gap: 12 }} alignItems='flex-start'>
                <ColourPickerSelect
                  value={values.colour}
                  onChange={(colour) => setFieldValue('colour', colour)}
                  label='Colour'
                  id='volunteer-tag-colour'
                />
                <Box flex={1}>
                  <Field variant='outlined' label='Tag Name' name='name' required autoFocus fullWidth />
                </Box>
              </Box>

              <Box marginTop='1.25rem'>
                <Field
                  variant='outlined'
                  label='Description'
                  name='description'
                  multiline
                  rows={5}
                  fullWidth
                  placeholder='What does this tag mean, and when should someone use it? Shown to managers when they pick tags.'
                />
              </Box>

              <Box marginTop='1.25rem'>
                <Autocomplete
                  freeSolo
                  options={existingGroups}
                  value={values.tagGroup}
                  onChange={(_unused, newValue) => setFieldValue('tagGroup', newValue ?? '')}
                  onInputChange={(_unused, newValue) => setFieldValue('tagGroup', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Group (optional)'
                      placeholder='e.g. Skills, Compliance'
                    />
                  )}
                />
              </Box>

              <Box display='flex' mt='18px' pt='10px'>
                <Switch
                  name='isArchived'
                  checked={values.isArchived}
                  onChange={() => setFieldValue('isArchived', !values.isArchived)}
                  inputProps={{ 'aria-label': 'archived checkbox' }}
                  color='primary'
                />
                <Box>
                  <Typography
                    style={{ fontSize: '13px', color: '#000000b0', fontWeight: 500, textTransform: 'uppercase' }}
                  >
                    {`Archived`}
                  </Typography>
                  <Typography style={{ fontSize: '12px', color: '#808080' }}>
                    {`Archived tags stay on the volunteers who already have them, but can no longer be added to anyone new.`}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {error && (
              <Box marginTop='1rem'>
                <Typography style={{ fontSize: '13px', color: '#d32f2f' }}>{error}</Typography>
              </Box>
            )}

            <Box display='flex' marginTop='1.25rem' width='100%' justifyContent='flex-end'>
              <TabletButton onClick={onClose} style={{ marginRight: '8px' }}>
                Cancel
              </TabletButton>
              <TabletButton variant='contained' type='submit' color='primary'>
                Done
              </TabletButton>
            </Box>
          </Form>
        )}
      </Formik>
    </FullscreenDialog>
  );
}
